/**
 * email.js — la forma dell'email di un paziente: UNA regola (Alex, 6/10/2026,
 * seguito del punto 29: «la forma dell'email si controlla solo nel browser»).
 *
 * LA REGOLA. Un'email vuota e' ammessa: l'email non e' obbligatoria (punto
 * 29). Un'email scritta deve avere la forma «qualcosa@qualcosa.qualcosa»,
 * senza spazi e con una sola chiocciola: e' l'espressione che il modulo
 * d'accettazione usava da prima, presa cosi' com'era, con il suo messaggio.
 * Non e' una verifica che l'indirizzo esista: dice solo che e' scritto come
 * un indirizzo.
 *
 * E AL MASSIMO 100 CARATTERI (Alex, 6/10/2026): la colonna della scheda e'
 * varchar(100), quella della coda del tablet varchar(150). Un'email piu'
 * lunga passava la coda e si fermava quando nasceva la scheda. Si contano i
 * caratteri come li conta PostgreSQL (i punti di codice, non le unita' UTF-16
 * di .length: un'emoji vale uno).
 *
 * DUE COPIE E UNA PROVA. Il browser non puo' importare dal server, quindi la
 * funzione vive qui e in backend/utils/email.js, uguale salvo le
 * esportazioni, come utils/codiceFiscale.js. Le confronta
 * frontend/scripts/verificaEmail.js, nel prebuild: chi cambia una copia
 * cambia l'altra, o la build non parte. Nel database la stessa regola e'
 * email_ammessa() (migration 313), che spella i caratteri di \s uno per uno;
 * backend/scripts/provaEmailFacoltativa.js la confronta con questa copia su
 * tutti i caratteri del piano di base.
 *
 * QUANDO SI GUARDA. Ogni strada che scrive l'email di un paziente la guarda
 * prima di scrivere, e rifiuta con 400 e MSG_EMAIL_FORMA (o MSG_EMAIL_LUNGA,
 * se e' scritta bene ma oltre EMAIL_MAX). Un'email gia' in
 * archivio senza forma (in archivio ce ne sono: si sono scritte prima di
 * questa regola) non ferma chi corregge un altro campo della scheda: si
 * guarda l'email che arriva nuova o cambiata, come il codice fiscale in
 * PUT /pazienti. Lo stesso fa il database, e lo stesso fanno i moduli nel
 * browser (Alex, 6/10/2026): un'email di prima senza forma, lasciata com'e',
 * non ferma niente e si segnala accanto al campo con MSG_EMAIL_DA_CORREGGERE
 * (avvisoEmail); cambiata in un'altra senza forma, si rifiuta
 * (erroreEmailCambiata). «Com'era» vuol dire uguale a quella della scheda;
 * vuota e assente sono la stessa cosa.
 */

export const MSG_EMAIL_FORMA = 'Formato email non valido';

/* Oltre il massimo il motivo e' la lunghezza, non la forma (rilievo del beta
   su a3be75e): un indirizzo scritto bene e troppo lungo non e' «scritto
   male», e chi legge deve sapere che cosa correggere. Il numero e' scritto
   per esteso: i manuali citano il messaggio alla lettera. Si guarda dopo la
   forma: un testo che non e' un indirizzo resta «Formato email non valido»
   anche se e' lungo. */
export const MSG_EMAIL_LUNGA = 'Email troppo lunga: al massimo 100 caratteri.';

export const MSG_EMAIL_DA_CORREGGERE = 'Email non valida: correggila o svuotala.';

/* La colonna della scheda, pazienti_tab.email varchar(100): la confronta
   provaEmailFacoltativa.js. Oltre, PostgreSQL si ferma con «value too long»
   (22001) PRIMA del trigger della 313, che quindi non puo' fare da rete per
   la lunghezza: questo controllo e' l'unico che risponde col motivo, e ogni
   strada lo fa prima di scrivere. */
export const EMAIL_MAX = 100;

export const FORMA_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Vero se il valore e' un'email ammessa: vuota, o con la forma e non oltre EMAIL_MAX caratteri. */
export function emailAmmessa(valore) {
    return erroreEmail(valore) === null;
}

/** Il messaggio del rifiuto (la forma, poi la lunghezza), oppure null se l'email e' ammessa. */
export function erroreEmail(valore) {
    if (valore === undefined || valore === null || valore === '') return null;
    if (typeof valore !== 'string' || !FORMA_EMAIL.test(valore)) return MSG_EMAIL_FORMA;
    if (Array.from(valore).length > EMAIL_MAX) return MSG_EMAIL_LUNGA;
    return null;
}

/* Uguale a quella della scheda: vuota e assente sono la stessa cosa. */
function stessaEmail(valore, dellaScheda) {
    const v = valore === undefined || valore === null ? '' : valore;
    const s = dellaScheda === undefined || dellaScheda === null ? '' : dellaScheda;
    return v === s;
}

/** Il rifiuto per un'email nuova o cambiata; null se e' quella della scheda o se e' ammessa. */
export function erroreEmailCambiata(valore, dellaScheda) {
    return stessaEmail(valore, dellaScheda) ? null : erroreEmail(valore);
}

/** L'avviso, non bloccante, per l'email della scheda rimasta com'era e senza forma; altrimenti null. */
export function avvisoEmail(valore, dellaScheda) {
    return stessaEmail(valore, dellaScheda) && !emailAmmessa(valore) ? MSG_EMAIL_DA_CORREGGERE : null;
}
